import React, { useState, useEffect, useContext } from 'react';
import {
  Grid,
  LinearProgress,
} from '@material-ui/core';
import { useParams, useLocation } from 'react-router-dom';
import {
  Page,
  Header,
  WarningPanel,
  CodeSnippet,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import BreadcrumbsNav from '../common/BreadcrumbsNav/BreadcrumbsNav';
import BackButton from '../utils/backButton';
import { PromotionContext } from '../../providers/PromotionDataProvider';
import { useCustomStyles } from '../leaderBoard/leaderBoardStyles';
import LeaderBoardCard from '../leaderBoard/Grid/leaderBoardCard';
import CategoryCard from '../leaderBoard/Grid/categoryCard';
import {
  GamificationApi,
  gamificationApiRef,
} from '../../apis/gamificationApis';
import {
  NotificationApi,
  notificationApiRef,
} from '../../apis/notificationApi';

interface CybersecurityUserDetails {
  email: string;
  displayname: string;
  score: number;
  levelname: string;
  levelid: string;
  tool: string;
  rewarddetails: RewardDetail[];
}

interface RewardDetail {
  rewardseventid: string;
  name: string;
  description: string;
  count: number;
  points: number;
  totalpoints: number;
}

interface CybersecurityLevel {
  id: string;
  type: string;
  name: string;
  description: string;
  minpoints: number;
  maxpoints: number;
}

export const CybersecurityUserDetailsPage = () => {
  const classes = useCustomStyles();
  const { username } = useParams<{ username: string }>();
  const location = useLocation();
  const state = location.state as { email: string } | undefined;
  
  const [userData, setUserData] = useState<CybersecurityUserDetails | null>(null);
  const [levels, setLevels] = useState<CybersecurityLevel[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filteredRewardDetails, setFilteredRewardDetails] = useState([]);
  
  const gamificationApi: GamificationApi = useApi(gamificationApiRef);
  const notificationApi: NotificationApi = useApi(notificationApiRef);
  const promotionContextData = useContext(PromotionContext);

  const userEmail = state?.email || `${username}@cbre.com`;

  const fetchUserDetails = () => {
    setIsLoading(true);
    gamificationApi.getCybersecurityUserDetails(userEmail)
      .then((res: any) => {
        if (res) {
          setUserData(res);
        }
        setIsLoading(false);
      })
      .catch((error: any) => {
        setIsLoading(false);
        notificationApi.sendNotification({
          message: `Error occurred - ${error?.message || 'Failed to fetch user details'}`,
          disapperAfterMs: 2500,
          severity: 'error',
        });
      });
  };

  const fetchLevels = () => {
    gamificationApi.getCybersecurityLevels()
      .then((res: any) => {
        if (res && Array.isArray(res)) {
          setLevels(res);
          promotionContextData.setGamificationlevelData(res);
        } else if (res && res.data && Array.isArray(res.data)) {
          setLevels(res.data);
          promotionContextData.setGamificationlevelData(res.data);
        } else {
          console.warn('Unexpected levels API response structure:', res);
          setLevels([]);
        }
      })
      .catch((error: any) => {
        console.error('Error fetching cybersecurity levels:', error);
        setLevels([]);
        notificationApi.sendNotification({
          message: `Error occurred - ${error?.message || 'Failed to fetch levels'}`,
          disapperAfterMs: 2500,
          severity: 'error',
        });
      });
  };

  const calculateActualLevel = (score: number) => {
    if (!levels || levels.length === 0) return null;
    
    // Sort levels by minpoints to ensure we check from lowest to highest
    const sortedLevels = [...levels].sort((a, b) => a.minpoints - b.minpoints);
    
    // Find the appropriate level based on score
    let currentLevel = sortedLevels[0]; // Default to first level
    
    for (const level of sortedLevels) {
      if (score >= level.minpoints && score <= level.maxpoints) {
        currentLevel = level;
        break;
      }
    }
    
    return currentLevel;
  };

  useEffect(() => {
    fetchLevels();
    fetchUserDetails();
    promotionContextData.setToggleHelpButton(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gamificationApi]);

  useEffect(() => {
    if (userData?.rewarddetails && userData.rewarddetails.length) {
      const filteredData = userData.rewarddetails.sort(
        (a: any, b: any) => b.totalpoints - a.totalpoints,
      );
      setFilteredRewardDetails(filteredData);
    }
  }, [userData?.rewarddetails]);

  // Calculate the actual level based on score
  const actualLevel = calculateActualLevel(userData?.score || 0);
  const userDataWithCalculatedLevel = userData ? {
    ...userData,
    levelname: actualLevel?.name || userData.levelname
  } : null;

  if (isLoading || !userData) {
    return (
      <Page themeId="home">
        <Header
          title="Cybersecurity User Details"
          pageTitleOverride="Cybersecurity User Details"
          subtitle={<BreadcrumbsNav lastRoutes={[]} skipLast={false} />}
        />
        <div className={classes.levelAndBadgesContainer}>
          <LinearProgress />
        </div>
      </Page>
    );
  }

  return (
    <Page themeId="home">
      <Header
        title="Cybersecurity User Details"
        pageTitleOverride="Cybersecurity User Details"
        subtitle={<BreadcrumbsNav lastRoutes={[]} skipLast={false} />}
      />
      <div className={classes.levelAndBadgesContainer}>
        <Grid container>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <BackButton />
          </Grid>

          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={12}
            className={classes.marginTop24}
          >
            <LeaderBoardCard data={userDataWithCalculatedLevel} selectedBtn="cumulative" />
          </Grid>
        </Grid>
        <Grid container className={classes.marginTop24}>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <h2 className={classes.categoryCardMargin20}>
              Cybersecurity Points Earned Category Wise
            </h2>
          </Grid>

          {filteredRewardDetails.map((item: any, index: number) => (
            <Grid item xs={12} md={4} key={`category${index}`}>
              <CategoryCard data={{
                ...item,
                points: item.totalpoints
              }} />
            </Grid>
          ))}
        </Grid>
      </div>
    </Page>
  );
};

export default CybersecurityUserDetailsPage;